// import core components
import { useEffect } from "react";
import { GetStaticProps } from "next";
import dynamic from "next/dynamic";
import Slider from "react-slick";
import { ScrollTrigger } from "gsap/dist/ScrollTrigger";
import parser from "html-react-parser";

// import custom component
import SEO from "@/elements/Shared/SEO";
import Hero from "@/elements/Hero";
const Form = dynamic(() => import("@/elements/Shared/Form"));
const StepCard = dynamic(() => import("@/elements/Shared/StepCard"));
const BlockTextImage = dynamic(
  () => import("@/elements/Shared/BlockTextImage")
);
const Button = dynamic(() => import("@/elements/Shared/Button"));
const Circle = dynamic(
  () => import("@/elements/Shared/DesignComponents/Circle")
);
const WhiteSquare = dynamic(
  () => import("@/elements/Shared/DesignComponents/WhiteSquare")
);
const DegradeEffect = dynamic(
  () => import("@/elements/Shared/DesignComponents/DegradeEffect")
);

// import interfaces
import MenuOptions from "@/interfaces/MenuOptions";

// import data
import career from "@/data/pages/career/index.json";

// import styles
import styles from "./career.module.scss";

// import utils
import getFormData from "@/utils/Forms/getFormData";

type CarrerProps = {
  menuOptions: MenuOptions;
  jobs: any;
  formsJobOptions: {
    title: string;
    slug: string;
  }[];
  formFieldsMap: any;
  formID: number;
};

export default function Career({
  menuOptions,
  jobs,
  formsJobOptions,
  formFieldsMap,
  formID,
}: CarrerProps) {
  const { hero, avantages, job_open, contact_job } = career;
  const seo = {
    title: "Archives carrières - District Web",
    description: "L'archives des postes à comblé",
  };

  const settings = {
    dots: true,
    arrows: false,
    infinite: false,
    speed: 500,
    slidesToScroll: 1,
    slidesToShow: 1,
    swipeToSlide: true,
    adaptiveHeight: false,
    responsive: [
      { breakpoint: 768, settings: { slidesToShow: 1 } },
      { breakpoint: 1023, settings: { slidesToShow: 2 } },
      { breakpoint: 10000000, settings: "unslick" },
    ],
  };

  if (process.browser) {
    ScrollTrigger.getAll().forEach((t) => {
      t.kill();
    });
  }
  useEffect(() => {
    menuOptions.setMenuColor("white");
  }, []);

  return (
    <>
      <SEO seo={seo} />
      <main className={styles["main"]}>
        <Hero
          data={hero}
          isSquareDesignVisible={false}
          dispositionContainer={7}
          pageName="career"
          heroId="hero--degrade"
        />
        <section className={styles["avantages"]}>
          <div className={`container ${styles["avantages--container"]}`}>
            <h2 className={styles["title"]}>Nos avantages</h2>

            <Slider {...settings} className={styles["avantages--slider"]}>
              {avantages.map(({ title, text }, i) => {
                return (
                  <StepCard
                    stepId={`avantages-step-${i + 1}`}
                    key={title + i}
                    title={title}
                    text={text}
                    stepNumber={i + 1}
                    className={styles["avantages--step-card"]}
                  />
                );
              })}
            </Slider>
          </div>
        </section>

        {jobs.length > 0 && (
          <section className={styles["job-open"]}>
            <div className={`container ${styles["job--container"]}`}>
              <h2 className={styles["title"]}>
                Postes à <strong>combler</strong>
              </h2>

              <table className={styles["job--table"]}>
                <tbody>
                  <tr>
                    <th className={`${styles["job--table-header"]}`}>Postes</th>

                    <th
                      className={`${styles["job--table-header"]} ${styles["job--header-description"]}`}
                    >
                      Description
                    </th>

                    <th className={styles["job--table-header"]}></th>
                  </tr>
                  {jobs.map((job, i) => {
                    return (
                      <tr key={`job-${i + 1}`}>
                        <td
                          className={`${styles["job--table-cell"]} ${styles["job--cell-title"]}`}
                        >
                          <p>{job.title.rendered}</p>
                        </td>

                        <td
                          className={`${styles["job--table-cell"]} ${styles["job--cell-description"]}`}
                        >
                          {parser(job.excerpt.rendered)}
                        </td>

                        <td className={styles["job--table-cell"]}>
                          <Button
                            isLink={true}
                            path={`/carrieres/${job.slug}`}
                            icon={true}
                          >
                            Détails
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <WhiteSquare
              isDefaultColor={false}
              isLighter={true}
              position="top-right"
            />
          </section>
        )}

        <section className={styles["job--contact"]}>
          <BlockTextImage
            content={contact_job.content}
            title={contact_job.title}
            HtagLevel={2}
            isImageAllEgal={true}
            dispositionContainer={19}
            sectionName="contact-job"
          >
            <div className={styles["job--contact-form"]}>
              <Form
                model="carriere"
                formJobsOptions={formsJobOptions}
                formFieldsMap={formFieldsMap}
                formID={formID}
              />
            </div>
          </BlockTextImage>
          <DegradeEffect size={858.58} position={9} />
          <Circle
            circle={{
              isAlternativeColor: false,
              isVisibleOnMobile: true,
              isAllSizeEgal: false,
              borderSize: "small",
              colorStyle: 5,
              styleNumber: 10,
            }}
            linearGradientId="carriere-1"
            pathId="carriere-path-1"
            circleId="carriere-circle-1"
          />
          <Circle
            circle={{
              isSvg: true,
              isAlternativeColor: false,
              isVisibleOnMobile: true,
              isAllSizeEgal: false,
              borderSize: "small",
              colorStyle: 2,
              styleNumber: 10,
            }}
            linearGradientId="carriere-2"
            pathId="carriere-path-2"
            circleId="carriere-circle-2"
          />
          <Circle
            circle={{
              isSvg: true,
              isAlternativeColor: false,
              isVisibleOnMobile: true,
              isAllSizeEgal: false,
              borderSize: "small",
              colorStyle: 5,
              styleNumber: 10,
            }}
            linearGradientId="carriere-3"
            pathId="carriere-path-3"
            circleId="carriere-circle-3"
          />
        </section>
      </main>
    </>
  );
}

export const getStaticProps: GetStaticProps = async () => {
  async function getAllJob() {
    return await fetch("https://data.districtweb.ca/wp-json/wp/v2/carrieres");
  }

  const jobsRes = await getAllJob();

  const jobs = await jobsRes.json();

  const formsJobOptions = [];

  for (const key in jobs) {
    formsJobOptions.push({
      title: jobs[key].title.rendered,
      slug: jobs[key].slug,
    });
  }

  const carrierForm = await getFormData(process.env.FORM_CARREER_ID);

  return {
    props: {
      jobs,
      formsJobOptions,
      formFieldsMap: carrierForm.fieldsMap,
      formID: carrierForm.formID,
    },
    revalidate: 60 * 60 * 24, // 1 days
  };
};
